<script lang="ts" setup>
import { computed, onMounted, ref, Ref } from 'vue';
import Chip from 'primevue/chip';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { AxiosResponse } from 'axios';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddOrganizationModal from '@/components/AddOrganizationModal';
import { PERMISSIONS } from '@/enum/permissions';
import { can } from '@/composables';
import { useOrganizationArchiveStore, useOrganizationStore } from '@/store';
import { OrganizationsInterface, SubOrganizationTableType } from '@/types';

const organizationStore = useOrganizationStore();
const archiveStore = useOrganizationArchiveStore();
const subOrganizations: Ref<SubOrganizationTableType[]> = ref([]);
const subOrganizationCount: Ref<number> = computed(() => (subOrganizations.value ? subOrganizations.value.length : 0));
const attachOrganizationModalOpen: Ref<boolean> = ref(false);
const organizationModalItems: Ref<OrganizationsInterface[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_SUB_ORGANIZATIONS])) getSubOrganizations();
});

const getSubOrganizations: (forceRequest?: boolean) => Promise<void> = async (forceRequest = false) => {
  const filterArchived = organizationStore.showArchived ? { archived: '1' } : undefined;
  await organizationStore
    .getSubOrganizationsForTable(forceRequest, filterArchived)
    .then((result) => (subOrganizations.value = result))
    .catch(() => (subOrganizations.value = []));
};

const createMenuModel = (item: SubOrganizationTableType) => {
  const menuItems = [
    {
      label: 'Archive',
      visible: can([PERMISSIONS.ORGANIZATION_ARCHIVE]),
      command: async () => {
        await archiveStore.archive(item.id as number).catch(() => {
          archiveStore.doToast(`Archiving failed`, 'Archived', true);
          return;
        });
        getSubOrganizations(true);
        archiveStore.doToast(`${item.name} has been archived`, 'Archived');
      },
    },
  ];

  if (item.archived_at) {
    menuItems[0] = {
      label: 'Unarchive',
      visible: can([PERMISSIONS.ORGANIZATION_ARCHIVE]),
      command: async () => {
        await archiveStore.unArchive(item.id as number).catch(() => {
          archiveStore.doToast(`Unarchiving failed`, 'Unarchived', true);
          return;
        });
        getSubOrganizations(true);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    };
  }
  return menuItems;
};

const attachSubOrganization: (ids: number[]) => Promise<void> = async (ids) => {
  const requests: Promise<void | AxiosResponse>[] = [];
  let failedRequest = 0;
  let detail = 'Organization(s) has been added';

  ids.forEach(async (id) => {
    const payload = organizationStore.organizations.data.find(
      (organization) => organization.key === id,
    ) as OrganizationsInterface;
    payload.data.parent_id = organizationStore.organization.id;
    requests.push(
      organizationStore.updateOrganization(payload.data, id).catch(() => {
        organizationStore.doToast('Adding Sub-organization failed', 'Add Sub-organization', true);

        failedRequest++;
        return;
      }),
    );
  });

  await Promise.all(requests);

  if (failedRequest) {
    if (ids.length > 1 && failedRequest !== ids.length) {
      detail = 'Some organzations were added, some failed to be added.';
    } else {
      return;
    }
  }

  getSubOrganizations(true);
  organizationStore.doToast(detail, 'Added');
};

const openAddOrganizations = async () => {
  organizationModalItems.value = await organizationStore.getAddOrganizationsModalData();
  attachOrganizationModalOpen.value = true;
};

const onCancelAdd = () => (attachOrganizationModalOpen.value = false);

const onSaveAdd = (subOrganizations: number[]) => {
  attachSubOrganization(subOrganizations);
  attachOrganizationModalOpen.value = false;
};
const showArchived = () => {
  organizationStore.showArchived = true;
  getSubOrganizations();
};
const hideArchived = () => {
  organizationStore.showArchived = false;
  getSubOrganizations();
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Sub-organizations</h2>
      <Chip>{{ subOrganizationCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATION_SHOW_ARCHIVE])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_UPDATE])"
          type="text"
          size="small"
          text="Add Sub-organizations"
          icon-left="add"
          @click="openAddOrganizations"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="subOrganizations"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-sub-organization-store-table"
        state-storage="session"
      >
        <template v-if="subOrganizationCount">
          <Column body-class="mw-20rem" header-class="mw-20rem" field="name" header="Organization" sortable>
            <template #body="{ data }">
              <RouterLink
                v-if="can([PERMISSIONS.ORGANIZATIONS_CONNECTIONS])"
                :to="`/organizations/${data.id}`"
                class="font-semibold"
                v-text="data.name"
              />
              <span v-else class="font-semibold" v-text="data.id" />
            </template>
          </Column>
          <Column field="" header="" />
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </template>
        <template #empty>Organizations not assigned</template>
      </DataTable>
      <AddOrganizationModal
        v-if="attachOrganizationModalOpen && can([PERMISSIONS.ORGANIZATION_UPDATE])"
        title="Add Sub-organizations"
        :items="organizationModalItems"
        :searchable="true"
        :multi-selection="true"
        @save="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
